/**
 * What a port's protection says, in words (website-backend docs/UplinkLossAndLoopProtection.md).
 *
 * A switch can shut a port down on its own (err-disable: loop protection, BPDU guard) and hold it
 * down. Over SNMP some platforms report that port exactly like an unplugged one, so its oper
 * status alone reads "down" and says nothing about why. `null`/absent is unread, never "fine": a
 * device whose profile doesn't read port protection says nothing here.
 */
export interface PortProtectionFacts {
  errDisabled?: boolean | null;
  errDisabledCause?: string | null;
  errDisabledSince?: string | null;
}

const CAUSES: Record<string, string> = {
  loop_protection: 'loop protection',
  bpdu_guard: 'BPDU guard',
};

/** "Shut by the device (loop protection)", or `null` when the port isn't shut by the device. */
export function portShutBy(facts: PortProtectionFacts): string | null {
  if (facts.errDisabled !== true) return null;
  const cause = facts.errDisabledCause ? CAUSES[facts.errDisabledCause] : undefined;
  return cause ? `Shut by the device (${cause})` : 'Shut by the device';
}

/** The tooltip under it: since when, and that it stays down until re-enabled or recovered. */
export function portShutDetail(facts: PortProtectionFacts): string | null {
  if (facts.errDisabled !== true) return null;
  const since = facts.errDisabledSince ? new Date(facts.errDisabledSince) : null;
  const when = since && !Number.isNaN(since.getTime()) ? ` since ${since.toLocaleString()}` : '';
  return `The device shut this port down on its own${when}. It stays down until it is re-enabled or the device's recovery timer brings it back.`;
}
